import React, { useCallback } from "react"
import { Table as ReactTable, useBlockLayout } from "react-table"
import { VariableSizeList } from "react-window"

import { SCROLLBAR_WIDTH } from "~/helpers/scrollbar"

import BaseTable from "./BaseTable"
import Row from "./Row"

type RowRendererProps = {
  index: number
  data: any
  style: Record<string, string | number>
}
type RowRendererFn = (
  props: RowRendererProps,
  fallback?: RowRendererFn,
) => React.ReactElement

const defaultRowRenderer: RowRendererFn = ({ index, data, style }) => {
  const row = data.table.rows[index]

  return (
    <Row
      key={index}
      row={row}
      table={data.table}
      style={style}
      setSize={data.setSize}
      index={index}
      evenRowClassName={data.evenRowClassName}
    />
  )
}

const TableVirtualized = ({
  table,
  height,
  minHeight,
  headerClassName,
  evenRowClassName,
  rowRenderer,
  emptyStateDisplay,
}: {
  table: ReactTable
  height: number
  minHeight: number
  headerClassName?: string
  evenRowClassName?: string
  rowRenderer?: RowRendererFn
  emptyStateDisplay?: React.ReactElement
}) => {
  const refs = React.useRef()

  const rowRendererCallback = useCallback(
    (props) =>
      rowRenderer
        ? rowRenderer(props, defaultRowRenderer)
        : defaultRowRenderer(props),
    [rowRenderer],
  )

  const sizeMap = React.useRef({})
  const setSize = React.useCallback((index, size) => {
    sizeMap.current = { ...sizeMap.current, [index]: size }
    //@ts-ignore
    refs.current.resetAfterIndex(index)
  }, [])

  const getSize = React.useCallback(
    (index: number) => {
      const newHeight = sizeMap.current[index]

      return Math.max(newHeight || minHeight, minHeight)
    },
    [minHeight],
  )

  if (!table.plugins.includes(useBlockLayout)) {
    console.warn("TableVirtualized requires useBlockLayout plugin!")
  }

  const itemData = React.useMemo(
    () => ({ table, setSize, evenRowClassName }),
    [table, setSize, evenRowClassName],
  )

  return (
    <BaseTable table={table} headerClassName={headerClassName}>
      {emptyStateDisplay && table.rows.length === 0 ? (
        <>{emptyStateDisplay}</>
      ) : (
        <VariableSizeList
          itemData={itemData}
          itemKey={(index, data) => data.table.rows[index].id}
          height={height}
          itemCount={table.rows.length}
          itemSize={getSize}
          width={
            table.totalColumnsWidth
              ? table.totalColumnsWidth + SCROLLBAR_WIDTH
              : undefined
          }
          ref={refs}
          overscanCount={10}
        >
          {rowRendererCallback}
        </VariableSizeList>
      )}
    </BaseTable>
  )
}

export default TableVirtualized
